/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/computePsmaPosVolumesFromBridge.js
 *
 * RECIP 1.0 E-2 — voxel-level PSMA+ volume computation.
 *
 * Replaces applyPsmaClassificationFromBridge.js (which computed per-ROI
 * per-timepoint PSMA(+)/(−) classification). That model was wrong per
 * Gafita 2022: PSMA-VOL = Σ (PSMA-positive voxels × voxel volume), NOT
 * Σ (PSMA-positive lesion CT volumes).
 *
 * NEW MODEL
 * ──────────
 * For each lesion/new-lesion ROI at each timepoint:
 *   - Read suvHistogram + petVoxelVolumeMm3 from bridge entry
 *   - Determine threshold: isBoneLesion → bone cutoff; else → liver formula
 *   - If soft-tissue + no liver threshold → psmaPosVolume = null (NE)
 *   - Else → psmaPosVolume = countVoxelsAboveThreshold(hist, thr) × voxelVol
 *
 * For blood-pool, liver-reference, other → psmaPosVolume = null (not applicable)
 *
 * psmaPosVolumeByTimepoint[ctSeriesUid] state semantics:
 *   number  (≥ 0): valid PSMA+ volume in mm³ (0 = all voxels below threshold)
 *   null:          NE — soft-tissue lesion with no liver reference at this tp,
 *                  OR SUV compute failed (no histogram), OR reference ROI
 *   'calculating': bridge entry not yet populated (still computing)
 *
 * RETURNS
 * ───────
 * { ROIContours, thresholds }
 *   ROIContours: deep clone with psmaPosVolumeByTimepoint populated
 *   thresholds:  { [ctSeriesUid]: { bone, softTissue, liverInputs } }
 *     same shape as D-model — passed to getRecipAssessments for legacy compat
 *     and for getRecipTimepointStats
 *
 * NOTE: psmaStatusByTimepoint, psmaOverrideByTimepoint, borderlineByTimepoint
 * are NOT set by this function. Callers should treat any leftover values on
 * stored ROIs as stale (they are never read by E-state code).
 */

import _ from 'lodash';
import { TRACKING_TYPES } from './guideline';
import { isBoneLesion } from './isBoneLesion';
import { computeRecipThresholdsAllTps } from './getRecipThresholds';
import { psmaPosVolumeMm3 } from './psmaPosVolumeUtils';
import { makeSuvKey, getSuvByKey } from '../suv/suvViewportBridge';

const log = (...args) => {
  // eslint-disable-next-line no-console
  console.log('[PSMA-VOL]', ...args);
};

/** Tracking types whose ROIs contribute to PSMA-VOL. */
const TUMOUR_TYPES = new Set([
  TRACKING_TYPES.LESION,
  TRACKING_TYPES.NEW_LESION,
]);

/**
 * Seed psmaPosVolumeByTimepoint with 'calculating' for all tumour ROIs
 * before SUV compute starts, so the UI can show a Computing… state.
 *
 * Pure function — returns a deep clone.
 *
 * @param {object} args
 * @param {Array}  args.ROIContours
 * @param {Array}  args.imageSets
 * @returns {Array}  cloned ROIContours
 */
export function seedCalculatingPsmaPosVolumes({ ROIContours, imageSets }) {
  const cloned = _.cloneDeep(ROIContours);
  const tpUids = imageSets
    .filter(s => s?.SeriesInstanceUID)
    .map(s => s.SeriesInstanceUID);
  for (const roi of cloned) {
    const vol = {};
    if (TUMOUR_TYPES.has(roi.trackingType)) {
      for (const uid of tpUids) vol[uid] = 'calculating';
    }
    roi.psmaPosVolumeByTimepoint = vol;
  }
  return cloned;
}

/**
 * Main entry — called after SUV compute completes.
 *
 * @param {object} args
 * @param {Array}  args.ROIContours
 * @param {Array}  args.imageSets
 * @param {object} args.criteria
 * @returns {{ ROIContours: Array, thresholds: object }}
 */
export default function computePsmaPosVolumesFromBridge({
  ROIContours,
  imageSets,
  criteria,
}) {
  // Compute per-tp thresholds (bone cutoff + liver formula soft-tissue)
  const thresholds = computeRecipThresholdsAllTps(
    imageSets,
    ROIContours,
    criteria
  );

  const summary = Object.entries(thresholds)
    .map(([uid, t]) => {
      const st = t.softTissue == null ? 'N/A' : t.softTissue.toFixed(2);
      return `${uid.slice(-8)} bone=${t.bone.toFixed(1)} st=${st}`;
    })
    .join(' | ');
  log(`thresholds: ${summary || '(none)'}`);

  const cloned = _.cloneDeep(ROIContours);

  for (const roi of cloned) {
    const psmaPosVolumeByTimepoint = {};

    for (const ctSet of imageSets) {
      if (!ctSet) continue;
      const ssUid = ctSet.rtstructSeriesInstanceUID;
      const ctSeriesUid = ctSet.SeriesInstanceUID;
      if (!ssUid || !ctSeriesUid) continue;

      // Non-tumour ROIs: not applicable
      if (!TUMOUR_TYPES.has(roi.trackingType)) {
        psmaPosVolumeByTimepoint[ctSeriesUid] = null;
        continue;
      }

      const key = makeSuvKey(ssUid, roi.ROINumber, ctSeriesUid);
      const entry = getSuvByKey(key);

      // No bridge entry yet → still computing
      if (!entry) {
        psmaPosVolumeByTimepoint[ctSeriesUid] = 'calculating';
        continue;
      }

      // Bridge entry present but histogram missing (compute failed or no contours)
      if (!entry.suvHistogram || entry.petVoxelVolumeMm3 == null) {
        psmaPosVolumeByTimepoint[ctSeriesUid] = null;
        continue;
      }

      const tpThresholds = thresholds[ctSeriesUid];

      if (isBoneLesion(roi)) {
        // Bone: absolute SUVmean cutoff (no liver reference needed)
        const thr = tpThresholds?.bone ?? 3.0;
        psmaPosVolumeByTimepoint[ctSeriesUid] = psmaPosVolumeMm3(
          entry.suvHistogram,
          thr,
          entry.petVoxelVolumeMm3
        );
      } else {
        // Soft-tissue: liver formula threshold
        const thr = tpThresholds?.softTissue;
        if (thr == null || !isFinite(thr)) {
          // No liver reference at this tp → NE for soft-tissue lesions
          psmaPosVolumeByTimepoint[ctSeriesUid] = null;
        } else {
          psmaPosVolumeByTimepoint[ctSeriesUid] = psmaPosVolumeMm3(
            entry.suvHistogram,
            thr,
            entry.petVoxelVolumeMm3
          );
        }
      }
    }

    roi.psmaPosVolumeByTimepoint = psmaPosVolumeByTimepoint;
  }

  log(
    'psmaPosVolume summary',
    cloned.map(r => ({
      roi: r.ROINumber,
      type: r.trackingType,
      vols: r.psmaPosVolumeByTimepoint,
    }))
  );

  return { ROIContours: cloned, thresholds };
}
